/**
 * Time clock admin section (#427 W1 — spec §5 W1c + D4/D5).
 *
 * Extracted from Staff.tsx (2026-07-14): this section — and every
 * server route it calls — is gated on payroll.clock:write:all, which
 * owner AND manager hold. But /staff is route-gated on
 * rbac-admin:write:all (owner-only), so the MANAGER the spec names as
 * the person who sets up the lobby tablet (W1 S5, Maddie) got "Access
 * restricted" and could never reach the registration UI. The section
 * now renders on the manager-reachable Punch Records page
 * (/timeclock/manage).
 *
 * Owns:
 *  - the kiosk device registry: register-this-device one-time-token
 *    flow (writes this browser's localStorage so /kiosk works the
 *    moment the manager signs out — spec D4) + two-tap revoke;
 *  - the shop-network allowlist (saving a non-empty list turns the
 *    restriction ON — the server rule, said out loud — spec D5);
 *  - per-staff kiosk PIN set/reset. The roster is fetched HERE via
 *    adminStaffApi.getStaff (manager-reachable: the GET is read-widened
 *    to booking-assign holders) — staff are picked by NAME, never a
 *    raw UUID (#278).
 *
 * The permission gate lives INSIDE the component (useAuth →
 * currentStaff, same derivation as the pages): non-holders render
 * nothing and no admin timeclock fetch ever fires.
 *
 * Design Philosophy: the HOST page keeps its single filled-primary
 * ("Add missed entry" on Punch Records) — section-level triggers
 * render secondary/ghost; filled-primary appears only inside this
 * section's modals. P5: last-seen times keep off-network token use
 * visible. Five states per card (skeleton / error-recovery / empty /
 * default; stale n/a — every mount refetches).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { Modal } from '@ferro/ui';
import {
  adminStaffApi,
  adminTimeclockApi,
  type AdminStaffUser,
  type TimeclockKiosk,
  type TimeclockSettingsView,
} from '../../lib/api.js';
import { useAuth } from '../../contexts/AuthContext.js';
import { effectiveRolesOf, rolesHavePermission } from '../../lib/rbac-matrix.js';
// Sawyer 14 early-clockin-gate (E1)
import { EarlyClockInCard } from './EarlyClockInCard.js';

/**
 * #427 W1 — the browser-local kiosk device token key. Registering a
 * kiosk from THIS browser writes the one-time token here so /kiosk on
 * the same device works immediately (spec D4 pairing flow).
 */
const KIOSK_DEVICE_TOKEN_KEY = 'ferro.kiosk.deviceToken';

function formatLastSeen(iso: string | null): string {
  if (!iso) return 'Never used';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never used';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

type TimeclockAdminSectionProps = {
  config: ClientConfig;
};

export function TimeclockAdminSection({
  config,
}: TimeclockAdminSectionProps): JSX.Element | null {
  const apiUrl = config.deployment.apiUrl;
  const { staff: currentStaff } = useAuth();

  // Gated on payroll.clock:write:all (owner + manager per the matrix) —
  // the same permission the server's kiosk/settings/PIN routes require,
  // so nobody sees a control the server would 403.
  const canManageTimeclock = rolesHavePermission(
    effectiveRolesOf(currentStaff),
    'payroll.clock:write:all',
  );

  const [kiosks, setKiosks] = useState<TimeclockKiosk[]>([]);
  const [kiosksLoading, setKiosksLoading] = useState(true);
  const [kiosksError, setKiosksError] = useState<string | null>(null);
  const [tcSettings, setTcSettings] = useState<TimeclockSettingsView | null>(
    null,
  );
  const [tcSettingsError, setTcSettingsError] = useState<string | null>(null);
  const [cidrDraft, setCidrDraft] = useState('');
  const [cidrPending, setCidrPending] = useState(false);
  const [tcNotice, setTcNotice] = useState<string | null>(null);
  const [tcError, setTcError] = useState<string | null>(null);
  // Register-this-device flow: name step → one-time-token success step.
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerName, setRegisterName] = useState('');
  const [registerPending, setRegisterPending] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registeredKiosk, setRegisteredKiosk] = useState<{
    name: string;
    token: string;
  } | null>(null);
  // Two-tap revoke (touch-friendly confirm — no hover, no window.confirm).
  const [revokeArmedId, setRevokeArmedId] = useState<string | null>(null);
  const [revokePendingId, setRevokePendingId] = useState<string | null>(null);
  // Per-staff kiosk PIN set/reset — roster fetched by this component
  // (the section no longer lives on the Staff page and can't borrow its
  // roster).
  const [pinRoster, setPinRoster] = useState<AdminStaffUser[]>([]);
  const [pinRosterLoading, setPinRosterLoading] = useState(true);
  const [pinRosterError, setPinRosterError] = useState<string | null>(null);
  const [pinFor, setPinFor] = useState<AdminStaffUser | null>(null);
  const [pinValue, setPinValue] = useState('');
  const [pinPending, setPinPending] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  const loadKiosks = async (): Promise<void> => {
    setKiosksLoading(true);
    setKiosksError(null);
    const result = await adminTimeclockApi.listKiosks(apiUrl);
    setKiosksLoading(false);
    if (result.error) {
      setKiosksError(result.error);
      return;
    }
    setKiosks(result.data?.kiosks ?? []);
  };

  const loadTcSettings = async (): Promise<void> => {
    setTcSettingsError(null);
    const result = await adminTimeclockApi.getSettings(apiUrl);
    if (result.error) {
      setTcSettingsError(result.error);
      return;
    }
    const settings = result.data?.settings ?? null;
    setTcSettings(settings);
    setCidrDraft((settings?.allowed_cidrs ?? []).join('\n'));
  };

  const loadPinRoster = async (): Promise<void> => {
    setPinRosterLoading(true);
    setPinRosterError(null);
    const result = await adminStaffApi.getStaff(apiUrl);
    setPinRosterLoading(false);
    if (result.error) {
      setPinRosterError(result.error);
      return;
    }
    setPinRoster((result.data?.staff ?? []).filter((s) => s.is_active));
  };

  const handleRegisterKiosk = async (): Promise<void> => {
    setRegisterPending(true);
    setRegisterError(null);
    const result = await adminTimeclockApi.registerKiosk(
      apiUrl,
      registerName.trim(),
    );
    setRegisterPending(false);
    if (result.error) {
      setRegisterError(result.error);
      return;
    }
    if (result.data) {
      // Pair THIS browser immediately (spec D4): /kiosk on this device
      // works as soon as the manager signs out.
      try {
        window.localStorage.setItem(
          KIOSK_DEVICE_TOKEN_KEY,
          result.data.device_token,
        );
      } catch {
        // Storage unavailable — the one-time token display still covers
        // manual setup on the target device.
      }
      setRegisteredKiosk({
        name: result.data.kiosk.name,
        token: result.data.device_token,
      });
      void loadKiosks();
    }
  };

  const closeRegister = (): void => {
    setRegisterOpen(false);
    setRegisterName('');
    setRegisterError(null);
    setRegisteredKiosk(null);
  };

  const handleRevokeKiosk = async (kiosk: TimeclockKiosk): Promise<void> => {
    if (revokeArmedId !== kiosk.id) {
      setRevokeArmedId(kiosk.id);
      return;
    }
    setRevokeArmedId(null);
    setRevokePendingId(kiosk.id);
    setTcError(null);
    const result = await adminTimeclockApi.revokeKiosk(apiUrl, kiosk.id);
    setRevokePendingId(null);
    if (result.error) {
      setTcError(result.error);
      return;
    }
    setTcNotice(
      `"${kiosk.name}" can no longer punch the clock. Register it again if this was a mistake.`,
    );
    void loadKiosks();
  };

  const handleSaveCidrs = async (): Promise<void> => {
    const cidrs = cidrDraft
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    setCidrPending(true);
    setTcError(null);
    const result = await adminTimeclockApi.updateSettings(apiUrl, {
      allowedCidrs: cidrs,
    });
    setCidrPending(false);
    if (result.error) {
      setTcError(result.error);
      return;
    }
    if (result.data?.settings) {
      setTcSettings(result.data.settings);
      setCidrDraft((result.data.settings.allowed_cidrs ?? []).join('\n'));
      // Mirror the server rule out loud (spec D5): a non-empty list
      // turns enforcement ON.
      setTcNotice(
        result.data.settings.ip_restriction_enabled
          ? 'Allowed networks saved — punches are now limited to these networks.'
          : 'Allowed networks saved. Restriction is off — punches work from any network.',
      );
    }
  };

  const handleDisableRestriction = async (): Promise<void> => {
    setCidrPending(true);
    setTcError(null);
    const result = await adminTimeclockApi.updateSettings(apiUrl, {
      ipRestrictionEnabled: false,
    });
    setCidrPending(false);
    if (result.error) {
      setTcError(result.error);
      return;
    }
    if (result.data?.settings) {
      setTcSettings(result.data.settings);
      setTcNotice('Network restriction turned off — punches work from any network.');
    }
  };

  const handleSetPin = async (): Promise<void> => {
    if (!pinFor) return;
    setPinPending(true);
    setPinError(null);
    const result = await adminTimeclockApi.setStaffPin(
      apiUrl,
      pinFor.id,
      pinValue,
    );
    setPinPending(false);
    if (result.error) {
      setPinError(result.error);
      return;
    }
    setTcNotice(
      `Kiosk PIN set for ${pinFor.first_name} ${pinFor.last_name}.`,
    );
    setPinFor(null);
    setPinValue('');
  };

  // Kiosk registry + timeclock settings + PIN roster (owner/manager
  // only; don't fire guaranteed-403 fetches for other roles). Depends
  // on the gate so a late auth hydration still loads exactly once.
  useEffect(() => {
    if (!canManageTimeclock) return;
    void loadKiosks();
    void loadTcSettings();
    void loadPinRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageTimeclock]);

  // Two-tap revoke disarms itself — an armed destructive button must
  // never linger waiting for an accidental second tap.
  useEffect(() => {
    if (revokeArmedId === null) return undefined;
    const id = window.setTimeout(() => setRevokeArmedId(null), 4_000);
    return () => window.clearTimeout(id);
  }, [revokeArmedId]);

  if (!canManageTimeclock) return null;

  return (
    <>
      {/* ── #427 W1 — Time clock (spec §5 W1c + D4/D5) ─────────────────
          Kiosk device registry (register / revoke), the shop-network
          allowlist (saving a non-empty list turns restriction ON — the
          server rule, said out loud), and per-staff kiosk PINs.
          P5: last-seen times keep off-network token use visible. */}
      <section
        className="ferro-timeclock-admin"
        aria-label="Time clock"
        data-testid="timeclock-admin-section"
      >
        <h2 className="ferro-subsection__title">Time clock</h2>

        {tcNotice ? (
          <div
            className="ferro-alert ferro-alert--success ferro-alert--dismissible"
            role="status"
          >
            <span>{tcNotice}</span>
            <button
              type="button"
              className="ferro-alert__dismiss"
              onClick={() => setTcNotice(null)}
            >
              Dismiss
            </button>
          </div>
        ) : null}
        {tcError ? (
          <div
            className="ferro-alert ferro-alert--error ferro-alert--dismissible"
            role="alert"
          >
            <span>{tcError}</span>
            <button
              type="button"
              className="ferro-alert__dismiss"
              onClick={() => setTcError(null)}
            >
              Dismiss
            </button>
          </div>
        ) : null}

        {/* ── Time clock devices ── */}
        <div className="ferro-tc-card">
          <div className="ferro-tc-card__head">
            <h3 className="ferro-tc-card__title">Time clock devices</h3>
            {/* Secondary on purpose: the host page owns the view's single
                filled-primary (P2); the filled confirm lives in the modal. */}
            <button
              type="button"
              className="ferro-btn ferro-btn--outline ferro-btn--sm"
              onClick={() => {
                setRegisterName('');
                setRegisterError(null);
                setRegisteredKiosk(null);
                setRegisterOpen(true);
              }}
            >
              Register this device
            </button>
          </div>
          {kiosksLoading ? (
            <div className="ferro-skeleton-stack" data-testid="tc-kiosks-skeleton">
              <div className="ferro-skeleton ferro-skeleton--stat" />
            </div>
          ) : kiosksError ? (
            <div
              className="ferro-alert ferro-alert--error ferro-alert--dismissible"
              role="alert"
            >
              <span>Couldn&rsquo;t load kiosk devices &mdash; {kiosksError}</span>
              <button
                type="button"
                className="ferro-alert__dismiss"
                onClick={() => void loadKiosks()}
              >
                Try again
              </button>
            </div>
          ) : kiosks.length === 0 ? (
            <p className="ferro-tc-card__empty">
              No kiosk yet. On the lobby tablet, sign in here and tap
              &ldquo;Register this device&rdquo; &mdash; staff then punch
              in with a name tap + PIN at <code>/kiosk</code>.
            </p>
          ) : (
            <ul className="ferro-tc-kiosk-list">
              {kiosks.map((k) => (
                <li
                  key={k.id}
                  className="ferro-tc-kiosk"
                  data-testid={`tc-kiosk-${k.id}`}
                >
                  <div className="ferro-tc-kiosk__info">
                    <span className="ferro-tc-kiosk__name">{k.name}</span>
                    <span className="ferro-tc-kiosk__meta">
                      {k.revoked_at
                        ? 'Revoked'
                        : `Last seen ${formatLastSeen(k.last_seen_at)}${
                            k.last_seen_ip ? ` · ${k.last_seen_ip}` : ''
                          }`}
                    </span>
                  </div>
                  {k.revoked_at ? (
                    <span className="ferro-status-pill ferro-status-pill--danger">
                      Revoked
                    </span>
                  ) : (
                    <button
                      type="button"
                      className={`ferro-btn ferro-btn--sm ${
                        revokeArmedId === k.id
                          ? 'ferro-btn--danger'
                          : 'ferro-btn--outline'
                      }`}
                      disabled={revokePendingId === k.id}
                      onClick={() => void handleRevokeKiosk(k)}
                    >
                      {revokePendingId === k.id
                        ? 'Revoking…'
                        : revokeArmedId === k.id
                          ? 'Tap again to revoke'
                          : 'Revoke'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Allowed networks (IP allowlist, spec D5) ── */}
        <div className="ferro-tc-card">
          <div className="ferro-tc-card__head">
            <h3 className="ferro-tc-card__title">Allowed networks</h3>
            {tcSettings ? (
              <span
                className={`ferro-status-pill ${
                  tcSettings.ip_restriction_enabled
                    ? 'ferro-status-pill--success'
                    : 'ferro-status-pill--pending'
                }`}
                data-testid="tc-ip-restriction-pill"
              >
                {tcSettings.ip_restriction_enabled
                  ? 'Restriction on'
                  : 'Restriction off'}
              </span>
            ) : null}
          </div>
          {tcSettingsError ? (
            <div
              className="ferro-alert ferro-alert--error ferro-alert--dismissible"
              role="alert"
            >
              <span>
                Couldn&rsquo;t load time-clock settings &mdash; {tcSettingsError}
              </span>
              <button
                type="button"
                className="ferro-alert__dismiss"
                onClick={() => void loadTcSettings()}
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              <p className="ferro-tc-card__hint">
                Limit punches to the shop&rsquo;s network. One network per
                line (e.g. <code>203.0.113.0/24</code>).{' '}
                <strong>
                  Saving one or more networks turns the restriction on
                </strong>{' '}
                &mdash; punches from anywhere else will be refused.
              </p>
              <textarea
                className="ferro-field__input ferro-tc-cidrs"
                aria-label="Allowed networks, one per line"
                value={cidrDraft}
                onChange={(e) => setCidrDraft(e.target.value)}
                rows={3}
                placeholder="203.0.113.0/24"
                disabled={tcSettings === null}
              />
              <div className="ferro-tc-card__actions">
                <button
                  type="button"
                  className="ferro-btn ferro-btn--outline ferro-btn--sm"
                  disabled={cidrPending || tcSettings === null}
                  onClick={() => void handleSaveCidrs()}
                >
                  {cidrPending ? 'Saving…' : 'Save networks'}
                </button>
                {tcSettings?.ip_restriction_enabled ? (
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--ghost ferro-btn--sm"
                    disabled={cidrPending}
                    onClick={() => void handleDisableRestriction()}
                  >
                    Turn restriction off
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>

        {/* Sawyer 14 early-clockin-gate (E1) — the early clock-in allowance.
            Shown while settings load (disabled, AC32) and when the server
            carries the field; hidden against an older server (AC21) or when
            the settings read failed (the alert above already says so). */}
        {tcSettingsError === null &&
        (tcSettings === null || 'early_clock_in_allowance_minutes' in tcSettings) ? (
          <EarlyClockInCard
            apiUrl={apiUrl}
            settings={tcSettings}
            onSaved={(settings, message) => {
              setTcSettings(settings);
              setTcNotice(message);
            }}
          />
        ) : null}

        {/* ── Kiosk PINs (per-staff set/reset, name-first — #278) ── */}
        <div className="ferro-tc-card">
          <div className="ferro-tc-card__head">
            <h3 className="ferro-tc-card__title">Kiosk PINs</h3>
          </div>
          <p className="ferro-tc-card__hint">
            Each person punches the kiosk with a 6-digit PIN. Staff can set
            their own from their login; set or reset one here when someone
            is locked out or brand new.
          </p>
          {pinRosterLoading ? (
            <div className="ferro-skeleton-stack" data-testid="tc-pins-skeleton">
              <div className="ferro-skeleton ferro-skeleton--stat" />
            </div>
          ) : pinRosterError ? (
            <div
              className="ferro-alert ferro-alert--error ferro-alert--dismissible"
              role="alert"
            >
              <span>Couldn&rsquo;t load the team &mdash; {pinRosterError}</span>
              <button
                type="button"
                className="ferro-alert__dismiss"
                onClick={() => void loadPinRoster()}
              >
                Try again
              </button>
            </div>
          ) : pinRoster.length === 0 ? (
            <p className="ferro-tc-card__empty">
              No active staff yet &mdash; invite your team under Staff, then
              set their kiosk PINs here.
            </p>
          ) : (
            <ul className="ferro-tc-pin-list">
              {pinRoster.map((s) => (
                <li key={s.id} className="ferro-tc-pin-row">
                  <span className="ferro-tc-pin-row__name">
                    {s.first_name} {s.last_name}
                  </span>
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--sm ferro-btn--outline"
                    onClick={() => {
                      setPinValue('');
                      setPinError(null);
                      setPinFor(s);
                    }}
                    aria-label={`Set kiosk PIN for ${s.first_name} ${s.last_name}`}
                  >
                    Set PIN
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ── #427 W1 — Register-this-device modal (spec D4 pairing) ── */}
      <Modal
        isOpen={registerOpen}
        onClose={closeRegister}
        title={registeredKiosk ? 'This device is set up' : 'Register this device'}
        closeOnBackdropClick={registeredKiosk === null}
      >
        {registeredKiosk ? (
          <div className="ferro-tc-registered" data-testid="tc-register-done">
            <p>
              <strong>&ldquo;{registeredKiosk.name}&rdquo;</strong> is now set
              up as a time clock <strong>on this browser</strong> &mdash;
              staff can punch in at{' '}
              <Link to="/kiosk" className="ferro-tc-registered__link">
                the kiosk screen
              </Link>{' '}
              as soon as you sign out.
            </p>
            <p className="ferro-tc-card__hint">
              Setting up a <em>different</em> device instead? Copy its device
              token now &mdash; it is shown only this once:
            </p>
            <code className="ferro-tc-token" data-testid="tc-device-token">
              {registeredKiosk.token}
            </code>
            <p className="ferro-tc-card__hint">
              Tip: lock punches to your shop&rsquo;s network under
              &ldquo;Allowed networks&rdquo; &mdash; saving a network turns
              the restriction on.
            </p>
            <div className="ferro-tc-modal-actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--primary"
                onClick={closeRegister}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form
            className="ferro-form"
            onSubmit={(e) => {
              e.preventDefault();
              void handleRegisterKiosk();
            }}
          >
            <p className="ferro-tc-card__hint">
              Do this on the tablet that will live at the counter &mdash;
              registering pairs <strong>this browser</strong> as the time
              clock.
            </p>
            <label className="ferro-field">
              <span className="ferro-field__label">Device name</span>
              <input
                type="text"
                className="ferro-field__input"
                value={registerName}
                onChange={(e) => setRegisterName(e.target.value)}
                placeholder="Front desk iPad"
                required
              />
            </label>
            {registerError ? (
              <p className="ferro-form__error" role="alert">
                {registerError}
              </p>
            ) : null}
            <div className="ferro-tc-modal-actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--outline"
                onClick={closeRegister}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="ferro-btn ferro-btn--primary"
                disabled={registerPending || registerName.trim().length === 0}
              >
                {registerPending ? 'Registering…' : 'Register'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* ── #427 W1 — Set-kiosk-PIN modal (exactly 6 digits) ── */}
      <Modal
        isOpen={pinFor !== null}
        onClose={() => setPinFor(null)}
        title={
          pinFor
            ? `Kiosk PIN — ${pinFor.first_name} ${pinFor.last_name}`
            : 'Kiosk PIN'
        }
      >
        {pinFor ? (
          <form
            className="ferro-form"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSetPin();
            }}
          >
            <label className="ferro-field">
              <span className="ferro-field__label">New 6-digit PIN</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                className="ferro-field__input ferro-tc-pin-input"
                value={pinValue}
                onChange={(e) =>
                  setPinValue(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                required
              />
            </label>
            {pinError ? (
              <p className="ferro-form__error" role="alert">
                {pinError}
              </p>
            ) : null}
            <div className="ferro-tc-modal-actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--outline"
                onClick={() => setPinFor(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="ferro-btn ferro-btn--primary"
                disabled={pinPending || !/^\d{6}$/.test(pinValue)}
              >
                {pinPending ? 'Saving…' : 'Save PIN'}
              </button>
            </div>
          </form>
        ) : null}
      </Modal>
    </>
  );
}

export default TimeclockAdminSection;
